/**
 * report-panel.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, HostListener, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Observable } from "rxjs/Rx";

import { AlgorithmStepService }
  from "../algorithms/management/algorithm-step.service";
import { StepUpdate } from "../algorithms/management/pipeline/step-update";
import { ReportStepService }
  from "../algorithms/management/report-step.service";
import { AlgorithmStep, IntakeStep, ReportStep }
  from "../algorithms/structure";
import { PrintService } from "../common/print.service";

import { ExplanationGraph } from "../graphs/explanation-graph";
import { GraphService } from "../graphs/graph.service";
import {
  ExplanationRequest,
  ExplanationResponse,
  PredecessorStepsRequest,
  PredecessorStepsResponse
} from "../server/messages/algorithms/graphs/";
import { MessagingService } from "../server/messaging.service";

import { Modal } from "angular2-modal/plugins/bootstrap";
import { ModalHelpers, SLOW_ATTACH, SLOW_REMOVAL } from "../support/utilities";
import { ReportDetailsModalComponent } from "./report-details-modal.component";

/**
 * A {@link ReportPanelComponent} presents a list of {@link ReportStep}s
 * obtained from a {@link ReportStepService}.
 */
@Component({
  selector: "report-panel",
  template: `
    <div class="panel panel-default">
      <div class="panel-heading" (click)="panelOpen = !panelOpen">
        <h3 class="panel-title">
          Reports
          <span class="badge nonprint">{{activeCount}}</span>
        </h3>
      </div>
      <table class="collapse" [class.in]="panelOpen">
        <tbody>
          <template ngFor let-step [ngForOf]="steps">
            <tr
              class="report border-between"
              [@slowRemoval]="!step.isActive ? 'remove' : 'keep'">
              <td>
                <badge [report]="step" (click)="showDetails(step)"></badge>
              </td>
              <td>
                <h4 (click)="step.hints.expanded = !step.hints.expanded">
                  <span
                    class="glyphicon"
                    [class.glyphicon-expand]="!step.hints.expanded"
                    [class.glyphicon-collapse-down]="step.hints.expanded">
                  </span>
                  {{step.displayName}}
                </h4>
                <p *ngIf="step.hints.expanded" [innerHtml]="step.presentation | unsafe" (click)="specialReportClick($event, step)"></p>
              </td>
            </tr>
          </template>

          <tr *ngIf="activeCount === 0" @slowAttach>
            <td colspan="2"><em>No reports available.</em></td>
          </tr>
        </tbody>
      </table>
    </div>
`,
  styles: [
    `badge {margin: 10px;}
    .badge-label {font-size: 1.2em;}
    .partial {
      display: inline-block;
      overflow: hidden;
      height: 1em;
      width: 100%;
      text-overflow: ellipsis;
		}
		.panel >>> .progress {background-color: #dadada;}
    `,
    ".panel table {padding: 5px; width: 100%;}"
  ],
  animations: [ SLOW_ATTACH, SLOW_REMOVAL ]
})
export class ReportPanelComponent implements OnInit
{
  /**
   * The {@link Array} of {@link ReportStep}s that should be presented by this
   * {@link ReportPanelComponent}.
   */
  private steps : ReportStep[] = [];

  /**
   * Whether the body of the panel is open or collapsed.
   */
  public panelOpen : boolean = true;

  public activeCount : number = 0;

  /**
   * Construct a new {@link ReportPanelComponent}.
   */
  constructor (
    private messaging : MessagingService,
    private reports : ReportStepService,
    private algorithmSteps : AlgorithmStepService,
    private graphs : GraphService,
    private modal : Modal,
    private print : PrintService,
    protected sanitizer : DomSanitizer
  )
  {
    /* Nothing. */
  }

  /**
   * Bypass the security mechanisms for rendering HTML from "user" JS code.
   * This is necessary in order to bind the step's presentation to the view.
   */
  public safeHtml (step : ReportStep) : SafeHtml
  {
    return this.sanitizer.bypassSecurityTrustHtml(step.presentation);
  }

  /**
   * Arrange to receive asynchronous {@link ReportStep}s from the {@link
   * ReportStepService}.
   */
  public ngOnInit () : void
  {
    this.steps = [];

    this.reports.updates().subscribe((update : StepUpdate<any, ReportStep>) => {
      const applied : boolean = update.findAndApply(this.steps);
      if (!applied && update.canCreate)
      {
        const addedStep : ReportStep = update.create();
        if (addedStep.hints.inline) {
          // The hint `inline` indicates that the report step should appear
          // within the main intake form instead of the report sidebar. These
          // options are exclusive, so we will hide the step from this view.
          return;
        }
        this.insertStep(addedStep);
      }

      // update the count
      this.activeCount = this.steps.reduce(
        ((count : number, step : ReportStep) =>
          step.isActive ? count + 1 : count),
        0
      );
    });

    this.print.printEvents.subscribe(() => this.openAll());
  }

  /**
   * Add a new report step into the correct order slot.
   *
   * @param newStep The ReportStep to be introduced
   */
  private insertStep (newStep : ReportStep) : void
  {
    /**
     * If the step is unordered, either by not having an order value (fall-back
     * support until the server guarantees this) or by having the sentinel max
     * int value, it is introduced as the last step.
     */
    if (!Number.isInteger(newStep.order)
      || newStep.order === Number.MAX_SAFE_INTEGER)
    {
      newStep.order = Number.MAX_SAFE_INTEGER; // Ensure we store it w/ sentinel
      this.steps.push(newStep);
      return;
    }

    /**
     * Otherwise put it in the right numeric slot relative to other orders.
     */
    let i : number = 0;
    while (i < this.steps.length)
    {
      if (newStep.order < this.steps[i].order)
      {
        this.steps.splice(i, 0, newStep);
        return;
      }
      i++;
    }

    // If we make it to the end, we must just have the highest ordered step.
    this.steps.push(newStep);
  }

  /**
   * Request the report's predecessor list from the server and attach it
   * directly to the {@link ReportStep} object
   * @param  {ReportStep} step
   * @return {ReportStep} the step passed in, for chaining
   */
  public getPredeccesors (step : ReportStep) : ReportStep
  {
    console.log("Seeking predecessors");
    const unknowns : number[] = [];
    this.messaging.send(new PredecessorStepsRequest([step.id]))
      .subscribe((m : PredecessorStepsResponse) => {
          step.predecessors = m.predecessors.map((preId : number) => {
            const predecessor : AlgorithmStep =
              this.algorithmSteps.get(preId);
            if (!predecessor)
            {
              unknowns.push(preId);
              return null;
            }
            return predecessor;
          }).filter((x : any) => x);

          if (unknowns.length > 0)
          {
            this.algorithmSteps.requestDefinitions(unknowns).subscribe(
              (steps : AlgorithmStep[]) => {
                step.predecessors = step.predecessors.concat(steps);
              });
          }
        });
    return step;
  }

  /**
   * Request the ExplanationGraph for a given step from the GraphService. This
   * data structure is necessary for the creation of the explanation window.
   * @param step The ReportStep being explained
   */
  public getExplanationGraph (step : ReportStep) : Observable<ExplanationGraph>
  {
    return this.graphs.explainStep(step);
  }

  /**
   * Open the report details modal dialog for a particular report
   * @param {ReportStep} step
   */
  public showDetails (step : ReportStep) : void
  {
    ModalHelpers.openCustom(
      this.modal,
      ReportDetailsModalComponent,
      {selected: step, explanation: this.getExplanationGraph(step)}
    );
  }

  public specialReportClick (event: MouseEvent & {target: HTMLElement}, step: ReportStep) : void
  {
      if (event.target.classList.contains("click-to-copy"))
      {
          (navigator as any).clipboard.writeText(event.target.innerText);
      }
  }

  /**
   * Expand every report in the panel.
   */
  public openAll () : void
  {
    this.steps.forEach(
      (step : ReportStep) => {
        (step as any)._expanded = true;
      });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/report-panel.component.ts